import Phaser from "phaser";
import { AUTOSAVE_MS, GAME_TITLE } from "@/game/data/constants";
import { messages } from "@/game/data/messages";
import { SceneKeys } from "@/game/data/scene-keys";
import { GameEvents } from "@/game/events/game-events";
import type { IdleState } from "@/game/model/idle-state";
import { IdleStage } from "@/game/objects/idle-stage";
import { applyClick, applyIdleTick } from "@/game/systems/idle-tick";
import { loadIdleState, saveIdleState } from "@/game/systems/save-system";
import { setHudHint } from "@/game/ui/dom-hud";

export class GameScene extends Phaser.Scene {
  private idleState!: IdleState;
  private stage!: IdleStage;
  private autosaveTimer = 0;

  constructor() {
    super(SceneKeys.Game);
  }

  create(): void {
    const { width, height } = this.scale;
    const now = Date.now();
    const { state, offlineSeconds } = loadIdleState(now);
    this.idleState = state;

    this.add
      .text(width / 2, 36, GAME_TITLE, {
        fontFamily: "system-ui, sans-serif",
        fontSize: "28px",
        color: "#e8eef5",
      })
      .setOrigin(0.5);

    this.stage = new IdleStage(this, width / 2, height * 0.55);
    this.stage.onClick(() => {
      this.idleState = applyClick(this.idleState);
      this.emitState();
      this.stage.pulse();
    });

    setHudHint(messages.tapHint);
    this.emitState();

    if (offlineSeconds > 0.5) {
      setHudHint(messages.offlineGain(offlineSeconds));
      window.dispatchEvent(
        new CustomEvent(GameEvents.OfflineApplied, {
          detail: { seconds: offlineSeconds },
        }),
      );
    }

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      saveIdleState(this.idleState);
    });
  }

  update(_time: number, delta: number): void {
    const deltaSeconds = delta / 1000;
    const { state } = applyIdleTick(this.idleState, deltaSeconds);
    this.idleState = state;
    this.emitState();

    this.autosaveTimer += delta;
    if (this.autosaveTimer >= AUTOSAVE_MS) {
      this.autosaveTimer = 0;
      saveIdleState(this.idleState);
    }
  }

  private emitState(): void {
    window.dispatchEvent(
      new CustomEvent(GameEvents.StateChanged, {
        detail: { state: this.idleState },
      }),
    );
  }
}
